import { useQuery } from '@tanstack/react-query';
import { api, type SessionRow, type EhrRow, type QueueStats } from '../../lib/api.js';
import { StatCard } from '../StatCard.js';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';

function SpotlightCard({
  label,
  value,
  perDay,
}: {
  label: string;
  value: number;
  perDay: number[];
}) {
  const chartData = perDay.map((v, i) => ({ i, v }));

  return (
    <div
      className="flex flex-col justify-between rounded-2xl px-5 py-4 col-span-2"
      style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(var(--glass-blur))',
        WebkitBackdropFilter: 'blur(var(--glass-blur))',
        border: '1px solid var(--glass-border)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
      }}
    >
      <div className="text-[11px] font-semibold uppercase tracking-widest text-text-secondary">{label}</div>
      <div className="flex items-end justify-between gap-4 mt-1.5">
        <div className="font-mono text-3xl font-bold tabular-nums text-text-primary leading-none">
          {value.toLocaleString()}
        </div>
        <div className="h-10 w-28 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barCategoryGap={2}>
              <Bar dataKey="v" radius={[2, 2, 0, 0]}>
                {chartData.map((_, index) => (
                  <Cell
                    key={index}
                    fill={index === chartData.length - 1 ? '#0dcfb8' : 'rgba(13,207,184,0.35)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="text-[10px] text-text-dimmed mt-1">last 7 days</div>
    </div>
  );
}

export function HeroStats() {
  const { data: sessionsData } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.listSessions(),
    refetchInterval: 15_000,
  });

  const { data: ehrsData } = useQuery({
    queryKey: ['ehrs'],
    queryFn: () => api.listEhrs(),
    refetchInterval: 60_000,
  });

  const { data: conflictCountData } = useQuery({
    queryKey: ['conflicts/count'],
    queryFn: () => api.getConflictCount(),
    refetchInterval: 30_000,
  });

  const { data: queueData } = useQuery({
    queryKey: ['queue/stats'],
    queryFn: () => api.getQueueStats(),
    refetchInterval: 5_000,
  });

  const { data: statsData } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 60_000,
  });

  const sessions: SessionRow[] = sessionsData?.sessions ?? [];
  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];
  const queue: QueueStats = queueData ?? { queued: 0, active: 0, delayed: 0, failed: 0 };
  const activeSessions = sessions.filter((s) => s.status === 'running').length;
  const conflictCount = conflictCountData?.count ?? 0;
  const recordsThisWeek = statsData?.recordsThisWeek ?? 0;
  const recordsPerDay = statsData?.recordsPerDay ?? [0, 0, 0, 0, 0, 0, 0];

  return (
    <div className="grid grid-cols-6 gap-4">
      <StatCard
        label="Active sessions"
        value={activeSessions}
        icon="▶"
        colorIndex={1}
      />
      <StatCard
        label="Queue depth"
        value={queue.queued + queue.active}
        delta={queue.failed > 0 ? { sign: '-', text: `${queue.failed} failed` } : undefined}
        icon="⧖"
        colorIndex={0}
      />
      <StatCard
        label="Open conflicts"
        value={conflictCount}
        icon="!"
        colorIndex={conflictCount > 0 ? 2 : 0}
      />
      <StatCard
        label="Registered EHRs"
        value={ehrs.length}
        icon="⬢"
        colorIndex={3}
      />
      <SpotlightCard
        label="Records imported · this week"
        value={recordsThisWeek}
        perDay={recordsPerDay}
      />
    </div>
  );
}
